import React, {
  ChangeEventHandler,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import './InputWrapper.css';
import Text from '../../Text/Text';
import {themmeColor} from 'src/constants/constants';
import {VALIDATOR, getValidatorFunctions} from '../../../Form.validators';
import {
  TRANSFORMERS,
  transform,
} from 'src/DesignSystem/Form/Forms.transformers';

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
};

const rightIconStyle: React.CSSProperties = {
  position: 'absolute',
  right: 12, // Position icon to the right
  pointerEvents: 'none', // Prevent icon from interfering with input interaction
};

const titleContainerStyle: React.CSSProperties = {
  marginBottom: 4,
  marginLeft: 2,
};

const inputContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  flex: 1,
};

const inputStyles: React.CSSProperties = {
  padding: '13px 12px',
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
  flex: 1,
};

export type InputWrapperBaseType = {
  title?: string;
  rightIcon?: ReactNode;
  validators?: VALIDATOR[];
  transformers?: TRANSFORMERS | TRANSFORMERS[];
  disabled?: boolean;
  disableValidation?: boolean;
};

type InputWrapperProps = {
  actualValue: any;
} & InputWrapperBaseType &
  React.InputHTMLAttributes<HTMLInputElement>;

const InputWrapper = ({
  title,
  rightIcon,
  validators = [],
  transformers = TRANSFORMERS.NONE,
  actualValue,
  disableValidation = false,
  ...props
}: InputWrapperProps) => {
  const style = useMemo(() => {
    if (!props.style) {
      return inputStyles;
    }
    return {...inputStyles, ...props.style};
  }, [props?.style]);

  const _onChangeValue = useCallback(
    (e: Parameters<ChangeEventHandler<HTMLInputElement>>[0]) => {
      e.target.value = transform(e.target.value, transformers);
      if (props.onChange) {
        props.onChange(e);
      }
    },
    [transformers, props.onChange],
  );

  const validatorFunctions = getValidatorFunctions(validators);
  const validationOutput = validatorFunctions.map((validator) => {
    return validator(actualValue);
  });

  const failedCases = validationOutput.filter((output) => {
    if (disableValidation) {
      return false;
    }
    return !output.isValid;
  });

  const isInvalid = failedCases.length > 0;

  return (
    <div style={containerStyle}>
      {title && (
        <Text
          type={Text.TEXT_TYPES.S}
          color={isInvalid ? themmeColor.errorRed : themmeColor.black60}
          text={title}
          containerStyle={titleContainerStyle}
        />
      )}
      <div>
        <div style={inputContainerStyle}>
          <input
            {...props}
            onChange={_onChangeValue}
            style={{
              ...style,
              borderColor: isInvalid ? themmeColor.errorRed : undefined,
            }}
            className="input-wrapper"
          />
          {rightIcon && <span style={rightIconStyle}>{rightIcon}</span>}
        </div>
        {isInvalid && (
          <>
            <div
              style={{
                fontSize: 13,
                color: themmeColor.errorRed,
                marginTop: 4,
              }}>
              Error value is {JSON.stringify(actualValue)}
            </div>
            <ul style={{marginBottom: 0, marginTop: 4}}>
              {failedCases.map((failedCase) => (
                <li style={{color: themmeColor.errorRed, fontSize: 11}}>
                  {failedCase.message}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default InputWrapper;
